/**
 * 2.0 wave 2, W2-CHROME (spec 7.5, CH-7) — where Settings was opened FROM, so
 * the back link can name its parent (Appendix C 2 and 79: "A tab never shows
 * back. A screen below a tab shows one back link, top left, named for its
 * parent.").
 *
 * On the native app a Training account has no Settings tab (`tabSetHasSettings`
 * is false); Settings opens from the avatar menu and is a screen below whichever
 * tab the user was on. `openSettingsFrom` records that tab in the navigation
 * state; `useSettingsOrigin` reads it back. Where the view's bar already holds
 * Settings, nobody shows the link and the origin is never read.
 *
 * Only a tab KEY travels in the state, never a label or a path, so a stale or
 * tampered history entry can name nothing but one of the four tabs below.
 */

import { useLocation, type NavigateFunction } from 'react-router-dom';

export interface SettingsOrigin {
  /** The tab's name: "Today", "Train", "Races" or "Team". */
  label: string;
  /** Where the back link goes. */
  to: string;
}

type OriginKey = 'today' | 'train' | 'races' | 'team';

const ORIGINS: Readonly<Record<OriginKey, SettingsOrigin>> = {
  today: { label: 'Today', to: '/today' },
  train: { label: 'Train', to: '/plan' },
  races: { label: 'Races', to: '/races' },
  team: { label: 'Team', to: '/team' },
};

/** The fallback: "Today" when unknown. */
export const DEFAULT_SETTINGS_ORIGIN: SettingsOrigin = ORIGINS.today;

const STATE_KEY = 'settingsOrigin';

function isOriginKey(value: unknown): value is OriginKey {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(ORIGINS, value);
}

/** The tab a pathname belongs to, or null when it is none of the four. */
export function originKeyForPath(pathname: string): OriginKey | null {
  if (pathname === '/' || pathname === '/today' || pathname.startsWith('/today/')) return 'today';
  if (pathname === '/plan' || pathname.startsWith('/plan/')) return 'train';
  if (pathname === '/races' || pathname.startsWith('/races/')) return 'races';
  if (pathname === '/team' || pathname.startsWith('/team/')) return 'team';
  return null;
}

/**
 * Open Settings and remember the tab we came from. A path that is not one of
 * the four tabs records nothing, so the link falls back to Today.
 */
export function openSettingsFrom(navigate: NavigateFunction, pathname: string): void {
  const key = originKeyForPath(pathname);
  navigate('/settings', key ? { state: { [STATE_KEY]: key } } : undefined);
}

/** The origin recorded by `openSettingsFrom`, else Today. */
export function useSettingsOrigin(): SettingsOrigin {
  const { state } = useLocation();
  const key = (state as Record<string, unknown> | null | undefined)?.[STATE_KEY];
  return isOriginKey(key) ? ORIGINS[key] : DEFAULT_SETTINGS_ORIGIN;
}
